import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";

/**
 * Keeps screens current when another device changes the business's data (FR-POS-10). The server
 * only says "something changed"; the query cache then refetches whatever is on screen, so idle
 * data is never fetched. Reconnects with backoff and pauses while offline or hidden.
 */
export function useLiveUpdates(tenantId: string): void {
  const queryClient = useQueryClient();
  useEffect(() => {
    let socket: WebSocket | null = null;
    let retry = 0;
    let reconnect: ReturnType<typeof setTimeout> | undefined;
    let ping: ReturnType<typeof setInterval> | undefined;
    let refresh: ReturnType<typeof setTimeout> | undefined;
    let stopped = false;

    const scheduleRefresh = () => {
      // Several quick sales refresh the screen once.
      if (refresh) return;
      refresh = setTimeout(() => {
        refresh = undefined;
        void queryClient.invalidateQueries({ queryKey: ["tenant", tenantId] });
      }, 400);
    };

    const connect = () => {
      if (stopped || !navigator.onLine || document.visibilityState === "hidden") return;
      const scheme = window.location.protocol === "https:" ? "wss" : "ws";
      socket = new WebSocket(`${scheme}://${window.location.host}/api/t/${tenantId}/live`);
      socket.onopen = () => {
        // Anything missed while disconnected is picked up now.
        if (retry > 0) scheduleRefresh();
        retry = 0;
        ping = setInterval(
          () => socket?.readyState === WebSocket.OPEN && socket.send("ping"),
          30_000,
        );
      };
      socket.onmessage = (event) => {
        if (typeof event.data === "string" && event.data.includes('"changed"')) scheduleRefresh();
      };
      socket.onclose = () => {
        clearInterval(ping);
        socket = null;
        if (stopped) return;
        const delay = Math.min(30_000, 1000 * 2 ** retry);
        retry += 1;
        reconnect = setTimeout(connect, delay);
      };
    };

    const wake = () => {
      if (!socket && !reconnect) connect();
      else if (!socket) {
        clearTimeout(reconnect);
        reconnect = undefined;
        connect();
      }
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") wake();
    };

    connect();
    window.addEventListener("online", wake);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      stopped = true;
      clearTimeout(reconnect);
      clearTimeout(refresh);
      clearInterval(ping);
      window.removeEventListener("online", wake);
      document.removeEventListener("visibilitychange", onVisibility);
      socket?.close(1000, "leaving");
    };
  }, [tenantId, queryClient]);
}
